import { useQuery } from '@tanstack/react-query'
import { Paperclip, UserRoundCheck } from 'lucide-react'
import { Link } from 'react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { downloadFile } from '@/api/files'
import { toApiError } from '@/api/errors'
import { ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { Drawer } from '@/components/ui/Drawer'
import { Skeleton } from '@/components/ui/Skeleton'
import { Can } from '@/features/auth/guards'
import { cn } from '@/utils/cn'
import { formatDateTime, humanize } from '@/utils/format'
import { leaveRequestQuery, useAdminCancelLeave } from '../api'
import { formatDays, formatRange, type LeaveBalanceImpact, type LeaveRequest } from '../types'
import { DecisionDialog } from './DecisionDialog'
import { LeaveStatusBadge, LeaveTypeBadge } from './LeaveBadges'

function BalanceImpact({ request, balance }: { request: LeaveRequest; balance: LeaveBalanceImpact }) {
  const { t } = useTranslation('leave')
  if (balance.allowance === null) {
    return (
      <p className="text-sm text-muted">{t('{{type}} has no yearly limit.', { type: request.type.name })}</p>
    )
  }
  const after = balance.afterApproval ?? 0
  const rows = [
    { label: t('Yearly allowance'), value: formatDays(balance.allowance) },
    { label: t('Already approved'), value: formatDays(balance.used) },
    { label: t('Pending (incl. this one)'), value: formatDays(balance.pending) },
    {
      label: request.status === 'pending' ? t('Left after approving') : t('Left (approved only)'),
      value: formatDays(Math.max(0, after)),
      strong: true,
    },
  ]
  return (
    <div className="space-y-3">
      <dl className="divide-y divide-line rounded-control border border-line">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between px-3 py-2 text-sm">
            <dt className="text-muted">{r.label}</dt>
            <dd className={cn('tabular', r.strong ? 'font-semibold text-ink' : 'text-ink-soft')}>
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
      {after < 0 && request.status === 'pending' ? (
        <Alert tone="danger" title={t('Over allowance')}>
          {t('Approving would exceed the yearly allowance by {{days}}.', { days: formatDays(-after) })}
        </Alert>
      ) : null}
    </div>
  )
}

/** Request details with balance impact and approve / reject / cancel actions. */
export function LeaveRequestDrawer({
  requestId,
  onOpenChange,
}: {
  requestId: number
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('leave')
  const query = useQuery(leaveRequestQuery(requestId))
  const cancel = useAdminCancelLeave()
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const r = query.data

  return (
    <Drawer
      open
      onOpenChange={onOpenChange}
      title={r ? r.person.name : t('Leave request')}
      description={
        r ? [humanize(r.kind), r.person.code, r.person.detail].filter(Boolean).join(' · ') : undefined
      }
      footer={
        r ? (
          <Can permission="leave.manage">
            {r.canCancel ? (
              <Button variant="ghost" onClick={() => setCancelling(true)}>
                {t('Cancel leave')}
              </Button>
            ) : null}
            {r.status === 'approved' && r.kind === 'staff' ? (
              <Can permission="academic.view">
                <Button asChild variant="secondary" leftIcon={<UserRoundCheck />}>
                  <Link to={`/admin/timetable/cover?leave=${r.id}`}>{t('Arrange cover')}</Link>
                </Button>
              </Can>
            ) : null}
            {r.status === 'pending' ? (
              <>
                <Button variant="danger-soft" onClick={() => setDecision('reject')}>
                  {t('Reject')}
                </Button>
                <Button onClick={() => setDecision('approve')}>{t('Approve')}</Button>
              </>
            ) : null}
          </Can>
        ) : undefined
      }
    >
      {query.error ? (
        <ErrorState compact onRetry={() => void query.refetch()} />
      ) : !r ? (
        <div className="space-y-3 p-5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-6 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <LeaveTypeBadge name={r.type.name} colour={r.type.colour} size="md" />
            <LeaveStatusBadge status={r.status} size="md" />
          </div>
          <DescriptionList
            items={[
              { label: t('Dates'), value: formatRange(r) },
              { label: t('Working days'), value: formatDays(r.days) },
              { label: t('Reason'), value: <span className="whitespace-pre-line">{r.reason}</span>, span: 2 },
              { label: t('Requested by'), value: r.requestedBy?.name ?? '—' },
              { label: t('Submitted'), value: formatDateTime(r.createdAt) },
              ...(r.decidedBy
                ? [
                    {
                      label: r.status === 'rejected' ? t('Rejected by') : t('Decided by'),
                      value: `${r.decidedBy.name} · ${formatDateTime(r.decidedAt)}`,
                    },
                  ]
                : []),
              ...(r.decisionNote
                ? [{ label: t('Decision note'), value: r.decisionNote, span: 2 as const }]
                : []),
              { label: t('Academic year'), value: r.academicYear },
            ]}
          />
          {r.attachment ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Paperclip />}
              onClick={() =>
                void downloadFile(r.attachment?.downloadUrl ?? '', r.attachment?.name ?? 'attachment')
              }
            >
              {r.attachment.name}
            </Button>
          ) : null}
          {r.balance ? (
            <section aria-labelledby="balance-impact-title">
              <h3 id="balance-impact-title" className="mb-2 text-sm font-semibold text-ink">
                {t('Balance impact · {{type}}', { type: r.type.name })}
              </h3>
              <BalanceImpact request={r} balance={r.balance} />
            </section>
          ) : null}
        </div>
      )}
      {decision && r ? (
        <DecisionDialog
          request={r}
          decision={decision}
          onOpenChange={(o) => (o ? undefined : setDecision(null))}
        />
      ) : null}
      <ConfirmDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t('Cancel this leave?')}
        description={r ? `${r.person.name} · ${formatRange(r)}` : undefined}
        confirmLabel={t('Cancel leave')}
        cancelLabel={t('Keep it')}
        loading={cancel.isPending}
        onConfirm={() =>
          cancel.mutate(requestId, {
            onSuccess: () => {
              toast.success(t('Leave cancelled'))
              setCancelling(false)
            },
            onError: (e) => toast.error(t('Could not cancel'), toApiError(e).message),
          })
        }
      />
    </Drawer>
  )
}
